<script lang="ts">
    import { getTopLevelClass, isSupplementary, type BookClass } from "@circleofa/acim-engine"
    import Button from "@components/button/Button.svelte"
    import EditableTabField from "./EditableTabField.svelte"
    import Pill from "@components/utilities/Pill.svelte"
    import { selectAndGotoTab, selectedTabID, type TabClass } from "@store/pageTracker"
    import { createEventDispatcher } from "svelte"
    import ReadingTabExpandedCardMenu from "./ReadingTabExpandedCardMenu.svelte"

    export let tab: TabClass

    type PillCategory = BookClass["dendrites"][number]

    const dispatch = createEventDispatcher<{ select: void }>()

    let pillCategory: PillCategory | null = null

    $: ({ displayName, nameWithoutEmoji, emoji, renderablePage, path, id } = tab)
    $: untitled = !nameWithoutEmoji
    $: pillCategory = renderablePage ? (getTopLevelClass(renderablePage) as PillCategory) : null
    $: selected = id === $selectedTabID

    function openTab() {
        selectAndGotoTab(tab.id)
        dispatch("select")
    }

    function handleSelectAreaKeydown(event: KeyboardEvent) {
        if (selected || event.target !== event.currentTarget) return
        if (event.key !== "Enter" && event.key !== " ") return
        event.preventDefault()
        openTab()
    }
</script>

<article class="tab-card" class:selected>
    {#if selected}
        <span class="selected-badge">Current</span>
    {/if}

    {#if selected}
        <div class="tab-select-area current-tab-details">
            <div class="tab-title-row" class:hasEmoji={Boolean(emoji)}>
                {#if emoji}
                    <span class="tab-icon" aria-hidden="true">
                        <span>{emoji}</span>
                    </span>
                {/if}
                <span class="tab-title-copy">
                    <span class="tab-label">Current tab</span>
                    <h2>
                        <EditableTabField {tab} kind="name" displayValue={displayName} />
                    </h2>
                </span>
            </div>

            {#if renderablePage}
                <div class="tab-location">
                    <div class="tab-location-line">
                        {#if pillCategory}
                            <Pill category={pillCategory} class="tab-pill" />
                        {/if}
                        {#if !isSupplementary(renderablePage)}
                            <span class="tab-path">
                                <EditableTabField {tab} kind="path" />
                            </span>
                        {/if}
                    </div>
                    <p>{renderablePage?.linkName[1]}</p>
                </div>
            {:else}
                <p class="tab-empty-location">Choose a place in the text for this tab.</p>
            {/if}
        </div>
    {:else}
        <div
            class="tab-select-area"
            role="button"
            tabindex="0"
            aria-pressed="false"
            on:keydown={handleSelectAreaKeydown}
            on:click={openTab}
        >
            <div class="tab-title-row" class:hasEmoji={Boolean(emoji)}>
                {#if emoji}
                    <span class="tab-icon" aria-hidden="true">
                        <span>{emoji}</span>
                    </span>
                {/if}
                <span class="tab-title-copy">
                    <span class="tab-label">Reading tab</span>
                    <h2 class:untitled>{displayName}</h2>
                </span>
            </div>

            {#if renderablePage}
                <div class="tab-location">
                    <div class="tab-location-line">
                        {#if pillCategory}
                            <Pill category={pillCategory} class="tab-pill" />
                        {/if}
                        {#if !isSupplementary(renderablePage)}
                            <span class="tab-path">{path}</span>
                        {/if}
                    </div>
                    <p>{renderablePage?.linkName[1]}</p>
                </div>
            {:else}
                <p class="tab-empty-location">Choose a place in the text for this tab.</p>
            {/if}
        </div>
    {/if}

    <div class="tab-card-actions">
        <Button color={selected ? "purple" : "white"} icon="arrow-right" go={openTab}>Read</Button>
        <ReadingTabExpandedCardMenu {tab} on:select={() => dispatch("select")} />
    </div>
</article>

<style>
    .tab-card {
        position: relative;
        display: grid;
        align-content: space-between;
        gap: 1rem;
        min-width: 0;
        min-height: 100%;
        border: 1px solid var(--color-purple-100);
        border-radius: 1.5rem;
        background: var(--color-grey-0);
        box-shadow: none;
        color: var(--color-grey-900);
        padding: 1rem;
        transition:
            border-color 0.16s ease,
            background-color 0.16s ease,
            transform 0.16s ease;
    }

    .tab-card:hover {
        border-color: var(--color-purple-200);
        box-shadow: none;
    }

    .tab-card.selected {
        border-color: var(--color-purple-300);
        background: color-mix(in srgb, var(--color-purple-50) 56%, var(--color-grey-0));
    }

    .selected-badge {
        position: absolute;
        top: 0.85rem;
        right: 0.85rem;
        z-index: 1;
        border-radius: 999px;
        background: var(--color-purple-100);
        color: var(--color-purple-700);
        font-size: 0.7rem;
        font-weight: 750;
        letter-spacing: 0.025em;
        line-height: 1;
        padding: 0.35rem 0.5rem;
        text-transform: uppercase;
    }

    .tab-select-area {
        display: grid;
        gap: 0.9rem;
        min-width: 0;
        padding-right: 0;
        cursor: pointer;
    }

    .tab-select-area.current-tab-details {
        cursor: default;
    }

    .tab-select-area:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 4px;
        border-radius: 1rem;
    }

    .tab-title-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        align-items: center;
        gap: 0.8rem;
        min-width: 0;
        padding-right: 4.8rem;
    }

    .tab-title-row.hasEmoji {
        grid-template-columns: auto minmax(0, 1fr);
    }

    .tab-icon {
        display: grid;
        place-items: center;
        width: 2.75rem;
        height: 2.75rem;
        border-radius: 999px;
        background: var(--color-purple-100);
        color: var(--color-purple-800);
        font-size: 1.35rem;
        line-height: 1;
    }

    .tab-icon :global(svg) {
        width: 1.15rem;
        height: 1.15rem;
    }

    .tab-title-copy {
        display: grid;
        gap: 0.25rem;
        min-width: 0;
    }

    .tab-label {
        color: var(--color-grey-500);
        font-size: 0.72rem;
        font-weight: 700;
        letter-spacing: 0.025em;
        line-height: 1;
        text-transform: uppercase;
    }

    h2,
    p {
        margin: 0;
    }

    h2 {
        min-width: 0;
        color: var(--color-grey-900);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 1.35rem;
        font-weight: 500;
        line-height: 1.25;
        letter-spacing: 0;
        overflow-wrap: anywhere;
    }

    h2.untitled {
        font-style: italic;
    }

    .tab-location {
        display: grid;
        gap: 0.55rem;
    }

    .tab-location-line {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
    }

    .tab-path {
        color: var(--color-purple-700);
        font-size: 0.86rem;
        font-weight: 750;
        line-height: 1.25;
    }

    .tab-location p,
    .tab-empty-location {
        color: var(--color-grey-600);
        font-size: 0.95rem;
        line-height: 1.55;
    }

    .tab-empty-location {
        color: var(--color-grey-500);
    }

    .tab-card-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.5rem;
        min-width: 0;
    }

    :global(.darkmode) .tab-card {
        border-color: var(--color-grey-800);
        background: var(--color-grey-800);
        box-shadow: none;
        color: var(--color-grey-50);
    }

    :global(.darkmode) .tab-card.selected {
        border-color: var(--color-grey-700);
        background: color-mix(in srgb, var(--color-grey-800) 76%, var(--color-purple-900));
    }

    :global(.darkmode) .selected-badge {
        background: var(--color-grey-700);
        color: var(--color-purple-100);
    }

    :global(.darkmode) .tab-icon {
        background: var(--color-grey-700);
        color: var(--color-purple-100);
    }

    :global(.darkmode) .tab-label {
        color: var(--color-grey-300);
    }

    :global(.darkmode) h2 {
        color: var(--color-grey-50);
    }

    :global(.darkmode) .tab-path {
        color: var(--color-purple-100);
    }

    :global(.darkmode) .tab-location p,
    :global(.darkmode) .tab-empty-location {
        color: var(--color-grey-200);
    }

    @media only screen and (max-width: 420px) {
        .tab-title-row {
            padding-right: 0;
        }

        .selected-badge {
            position: static;
            justify-self: start;
            order: -1;
        }

        .tab-card-actions {
            justify-content: stretch;
        }

        .tab-card-actions :global(.app-button) {
            flex: 1 1 auto;
        }
    }
</style>
